import { MouseEvent, useState } from 'react';

import { Box, Popover, Stack, Typography } from '@mui/material';

export type ResourceAttributes = { [key: string]: string | undefined } | undefined;

export function getAttributeEntries(attributes: ResourceAttributes): [string, string][] {
	return Object.entries(attributes ?? {}).map(([key, value]) => [key, value ?? '']);
}

type AttributeCountBadgeProps = {
	attributes: ResourceAttributes;
};

export default function AttributeCountBadge({ attributes }: AttributeCountBadgeProps) {
	const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
	const entries = getAttributeEntries(attributes);

	const handleMouseEnter = (event: MouseEvent<HTMLElement>) => {
		if (entries.length > 0) {
			setAnchorEl(event.currentTarget);
		}
	};

	const handleMouseLeave = () => {
		setAnchorEl(null);
	};

	return (
		<Box onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} sx={{ display: 'inline-flex', alignItems: 'center', height: '100%' }}>
			<Box
				sx={{
					width: 28,
					height: 28,
					borderRadius: '50%',
					display: 'inline-flex',
					alignItems: 'center',
					justifyContent: 'center',
					bgcolor: entries.length > 0 ? 'primary.main' : 'action.disabledBackground',
					color: entries.length > 0 ? 'primary.contrastText' : 'text.secondary',
					fontSize: 13,
					fontWeight: 600,
					lineHeight: 1,
				}}
			>
				{entries.length}
			</Box>
			<Popover
				open={!!anchorEl}
				anchorEl={anchorEl}
				onClose={handleMouseLeave}
				disableRestoreFocus
				sx={{ pointerEvents: 'none' }}
				anchorOrigin={{ vertical: 'center', horizontal: 'right' }}
				transformOrigin={{ vertical: 'center', horizontal: 'left' }}
				PaperProps={{ sx: { p: 1.5, ml: 1, maxWidth: 420 } }}
			>
				<Stack spacing={0.75}>
					{entries.map(([key, value]) => (
						<Box key={key} sx={{ display: 'grid', gridTemplateColumns: 'minmax(120px, max-content) minmax(120px, 1fr)', gap: 1.5 }}>
							<Typography variant="caption" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
								{key}
							</Typography>
							<Typography variant="caption" sx={{ overflowWrap: 'anywhere' }}>
								{value}
							</Typography>
						</Box>
					))}
				</Stack>
			</Popover>
		</Box>
	);
}
